import { useCallback, useEffect, useState } from 'react';
import type { WorldLaunchHandoff } from '../CreateWorld/draftStorage.js';
import {
  buildRuntimeView,
  dismissMissedPromptState,
  initRuntimeState,
  resumeRuntimeState,
  tickRuntimeState,
} from './adapter.js';
import { saveRuntimeState } from './persistence.js';
import type { AquariumRuntimeView, PersistedRuntimeState } from './types.js';
import { catchUp, type CatchUpResult } from './catchUp.js';

// One catch-up per absence. StrictMode remounts this hook; the second pass must
// reuse the request instead of seeing a visit stamp that wiped the gap.
const catchUpInflight = new Map<string, Promise<CatchUpResult>>();

const NARROW_BREAKPOINT = 720;

function useNarrowViewport(): boolean {
  const [narrow, setNarrow] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth < NARROW_BREAKPOINT : false,
  );
  useEffect(() => {
    const onResize = (): void => setNarrow(window.innerWidth < NARROW_BREAKPOINT);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return narrow;
}

export function useAquariumRuntime(handoff: WorldLaunchHandoff | null): {
  view: AquariumRuntimeView | null;
  runtimeState: PersistedRuntimeState | null;
  setRuntimeState: React.Dispatch<React.SetStateAction<PersistedRuntimeState | null>>;
  setFocusedCharacterId: (id: string | null) => void;
  dismissMissedPrompt: () => void;
  tick: (deltaMs: number) => void;
  persistVisit: () => void;
} {
  const narrow = useNarrowViewport();
  const [runtimeState, setRuntimeState] = useState<PersistedRuntimeState | null>(null);
  const [focusedCharacterId, setFocusedCharacterId] = useState<string | null>(null);
  const [showMissed, setShowMissed] = useState(false);
  const [catchingUp, setCatchingUp] = useState(false);

  useEffect(() => {
    if (!handoff) return;
    initRuntimeState(handoff);
    // The main menu does not mount this hook. Leave lastVisitedAt alone until
    // catch-up has read the gap, then stamp only while this world stays loaded.
    const resumed = resumeRuntimeState(handoff, { stampVisit: false });
    setRuntimeState(resumed.state);
    setShowMissed(resumed.showMissedPrompt);
    const worldId = handoff.snapshot.worldId;
    const absenceKey = `${worldId}:${resumed.state.lastVisitedAt}`;
    let active = true;
    const stampLoaded = (): void => {
      if (!active) return;
      setRuntimeState((current) => {
        if (!current) return current;
        const next = { ...current, lastVisitedAt: new Date().toISOString() };
        saveRuntimeState(next);
        return next;
      });
    };
    if (resumed.awayMs <= 0) {
      stampLoaded();
      return () => { active = false; };
    }
    let pending = catchUpInflight.get(absenceKey);
    if (!pending) {
      pending = catchUp({
        worldId,
        elapsedRealMs: resumed.awayMs,
        lastEvents: resumed.state.historyEvents,
        locationDescription: handoff.snapshot.connected.description,
        roster: handoff.roster.map((npc) => ({ id: npc.id, firstName: npc.firstName })),
        relationships: (handoff.snapshot.characterBonds ?? []).map((bond) => ({
          familiarity: bond.closeness,
          affinity: 0,
        })),
        existing: resumed.state.historyEvents,
        endIso: resumed.state.clock.inWorldIso,
        schedule: handoff.snapshot.schedule,
        locationRegion: handoff.snapshot.location.region,
      });
      catchUpInflight.set(absenceKey, pending);
    }
    setCatchingUp(true);
    void pending.then((result) => {
      if (!active) return;
      const added = result.events.slice(resumed.state.historyEvents.length);
      if (added.length === 0 && !result.resume) return;
      setRuntimeState((current) => {
        if (!current) return current;
        const next = {
          ...current,
          historyEvents: result.events,
          eventObservations: [
            ...current.eventObservations,
            ...added.map((event) => ({
              eventId: event.eventId,
              userObserved: false,
              missedWhileAway: true,
            })),
          ],
          resumePose: result.resume ?? undefined,
          ...(added.length > 0 ? { missedEventStory: result.eventStory } : {}),
        };
        saveRuntimeState(next);
        return next;
      });
      setShowMissed(true);
    }).finally(() => {
      if (!active) return;
      setCatchingUp(false);
      stampLoaded();
    });
    return () => {
      active = false;
    };
  }, [handoff]);

  useEffect(() => {
    if (!handoff) return;
    // While the world is loaded it is live. The main menu unmounts this hook,
    // so this stamp stops and the next load can catch up the gap.
    const stamp = (): void => {
      setRuntimeState((current) => {
        if (!current) return current;
        const next = { ...current, lastVisitedAt: new Date().toISOString() };
        saveRuntimeState(next);
        return next;
      });
    };
    const interval = window.setInterval(stamp, 60_000);
    window.addEventListener('pagehide', stamp);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener('pagehide', stamp);
    };
  }, [handoff]);

  const tick = useCallback(
    (deltaMs: number) => {
      if (!handoff || deltaMs <= 0) return;
      setRuntimeState((cur) => {
        if (!cur) return cur;
        return tickRuntimeState(cur, handoff.snapshot, deltaMs);
      });
    },
    [handoff],
  );

  const persistVisit = useCallback(() => {
    setRuntimeState((cur) => {
      if (!cur) return cur;
      const next = { ...cur, lastVisitedAt: new Date().toISOString() };
      saveRuntimeState(next);
      return next;
    });
  }, []);

  const dismissMissedPrompt = useCallback(() => {
    setShowMissed(false);
    setRuntimeState((cur) => (cur ? dismissMissedPromptState(cur) : cur));
  }, []);

  useEffect(() => {
    if (!handoff) return;
    let interval = 0;
    let last = Date.now();
    const punch = (): void => {
      const now = Date.now();
      const delta = now - last;
      last = now;
      if (delta <= 0) return;
      setRuntimeState((cur) => (cur ? tickRuntimeState(cur, handoff.snapshot, delta) : cur));
    };
    const timeout = window.setTimeout(() => {
      punch();
      interval = window.setInterval(punch, 60_000);
    }, 60_000 - (Date.now() % 60_000));
    return () => {
      window.clearTimeout(timeout);
      window.clearInterval(interval);
    };
  }, [handoff]);

  const view =
    handoff && runtimeState
      ? buildRuntimeView({
          handoff,
          state: runtimeState,
          showMissedPrompt: showMissed,
          focusedCharacterId,
          isNarrowViewport: narrow,
          catchingUp,
        })
      : null;

  return {
    view,
    runtimeState,
    setRuntimeState,
    setFocusedCharacterId,
    dismissMissedPrompt,
    tick,
    persistVisit,
  };
}
